import { isControlledSchedule } from '../utils/dea-schedule';
import type {
	OrderCheckFiringLevel,
	OrderCheckFlag,
	OrderCheckLevel,
} from './order-check-level';
import {
	isFlagOn,
	isOrderCheckOn,
	isOrderedQuantity,
} from './order-check-level';

/**
 * PD-16276 — what a ship-to address may receive, by DEA schedule, and the
 * controlled-substance share of an order.
 *
 * The schedule restriction is a hard rule in every channel, whatever the
 * submission-check preferences say: an Rx line needs the address's Rx flag,
 * a C2 line its schedule 2 flag, and so on. It is strict on purpose. There is
 * no "either flag will do" and no per-tenant allowance; a flag that is wrong
 * in the ERP blocks the order until the customer's data is fixed.
 *
 * The storefront adds one more hard rule on top: Schedule I and II lines are
 * not sold online (see ONLINE_ORDERING_BLOCKED_SCHEDULES), unless the
 * storefront site allows Schedule II ordering (PD-16776).
 */

/** The ship-to address's permission flags. A null flag means "not allowed". */
export interface ScheduleFlagsModel {
	isRxAllowed: OrderCheckFlag;
	isSchedule2Allowed: OrderCheckFlag;
	isSchedule2NAllowed: OrderCheckFlag;
	isSchedule3Allowed: OrderCheckFlag;
	isSchedule3NAllowed: OrderCheckFlag;
	isSchedule4Allowed: OrderCheckFlag;
	isSchedule5Allowed: OrderCheckFlag;
}

export type ScheduleFlagName = keyof ScheduleFlagsModel;

/**
 * The comparable form of a stored schedule. Legacy imports left mixed-case
 * values ('Rx', 'c5'); an unscheduled item is OTC. Matching is exact on the
 * uppercased value, the same comparison the CRM cart has always made.
 */
export function normalizeDeaSchedule(
	schedule: string | null | undefined,
): string {
	return (schedule ?? 'OTC').toUpperCase();
}

/**
 * The ship-to flag a schedule needs, or null when the address flags do not
 * restrict it (OTC, C1 and anything unrecognized).
 */
export function scheduleRestrictionFlag(
	schedule: string | null | undefined,
): ScheduleFlagName | null {
	switch (normalizeDeaSchedule(schedule)) {
		case 'RX':
			return 'isRxAllowed';
		case 'C2':
			return 'isSchedule2Allowed';
		case 'C2N':
			return 'isSchedule2NAllowed';
		case 'C3':
			return 'isSchedule3Allowed';
		case 'C3N':
			return 'isSchedule3NAllowed';
		case 'C4':
			return 'isSchedule4Allowed';
		case 'C5':
			return 'isSchedule5Allowed';
		default:
			return null;
	}
}

/**
 * The single order-level error for a ship-to address that no longer resolves
 * to a live record (for example one retired by the ERP address sync). The CRM
 * cart shows it for any cart; the server raises it when a line needs the
 * address's flags, since without them it cannot tell what may ship there.
 */
export const SHIPPING_ADDRESS_NOT_FOUND_MESSAGE =
	'Shipping address not found — please reselect the shipping address';

export function buildScheduleRestrictionMessage(schedule: string): string {
	return `${schedule} items not allowed`;
}

export interface ScheduleLineModel {
	itemId: number;
	quantity: number;
	/** The item's effective schedule at the ship-to state (state row beats Federal). */
	schedule: string | null | undefined;
}

export interface ScheduleRestrictionFindingModel {
	itemId: number;
	/** The normalized schedule that is not allowed. */
	schedule: string;
	flag: ScheduleFlagName;
	message: string;
}

/**
 * Lines whose schedule the ship-to address does not allow. Always a hard
 * error; the caller does not pass a level.
 */
export function checkScheduleRestrictions(
	lines: ReadonlyArray<ScheduleLineModel>,
	flags: ScheduleFlagsModel,
): Array<ScheduleRestrictionFindingModel> {
	const findings: Array<ScheduleRestrictionFindingModel> = [];

	for (const line of lines) {
		if (!isOrderedQuantity(line.quantity)) {
			continue;
		}

		const flag = scheduleRestrictionFlag(line.schedule);

		if (flag == null || isFlagOn(flags[flag])) {
			continue;
		}

		const schedule = normalizeDeaSchedule(line.schedule);
		findings.push({
			itemId: line.itemId,
			schedule,
			flag,
			message: buildScheduleRestrictionMessage(schedule),
		});
	}

	return findings;
}

/**
 * Schedules the storefront does not sell by default. Ordering a Schedule I or
 * II substance requires a DEA Form 222 (or CSOS, its electronic form), and
 * Workd has no CSOS integration, so these lines are refused on the ecommerce
 * channel whatever the ship-to flags allow. The CRM is unaffected: a
 * rep-placed order is covered by the Form 222 the customer completes outside
 * Workd.
 *
 * PD-16776 — a storefront site may allow Schedule II ordering
 * (`PortalSiteSettings.allowScheduleIIOrdering`). Its tenant then takes the
 * order online and completes the Form 222 with the customer afterwards,
 * outside Workd, the same way a CRM order works. Schedule I has no lawful
 * commercial sale, so it stays refused on every storefront.
 */
export const ONLINE_ORDERING_BLOCKED_SCHEDULES: ReadonlySet<string> = new Set([
	'C1',
	'C2',
	'C2N',
]);

/**
 * The blocked schedules a storefront site may choose to sell (PD-16776).
 */
export const SCHEDULE_II_SCHEDULES: ReadonlySet<string> = new Set([
	'C2',
	'C2N',
]);

/** What the storefront site allows beyond the default block. */
export interface OnlineOrderingOptionsModel {
	/**
	 * The site's `allowScheduleIIOrdering`. Absent or false keeps the default
	 * block, so a caller that does not know the site fails closed.
	 */
	allowScheduleII?: boolean;
}

/**
 * Whether the storefront may not sell an item with this schedule. The
 * storefront's catalog, product page, suggestions and add-to-cart read the
 * item's Federal schedule through this, so the item is stopped before it
 * reaches the cart; the cart and submit checks below remain the gate.
 */
export function isOnlineOrderingBlockedSchedule(
	schedule: string | null | undefined,
	options?: OnlineOrderingOptionsModel,
): boolean {
	const normalized = normalizeDeaSchedule(schedule);

	if (!ONLINE_ORDERING_BLOCKED_SCHEDULES.has(normalized)) {
		return false;
	}

	return !(
		options?.allowScheduleII === true && SCHEDULE_II_SCHEDULES.has(normalized)
	);
}

export function buildOnlineOrderingBlockedMessage(schedule: string): string {
	return `${schedule} items cannot be ordered online`;
}

export interface OnlineOrderingFindingModel {
	itemId: number;
	/** The normalized schedule that cannot be ordered online. */
	schedule: string;
	message: string;
}

/**
 * Lines the storefront may not sell. Always a hard error. A line may be passed
 * more than once with different schedules (the server passes both the
 * effective schedule at the ship-to state and the Federal one, since Form 222
 * follows the Federal schedule); an item yields at most one finding.
 */
export function checkOnlineOrderingRestrictions(
	lines: ReadonlyArray<ScheduleLineModel>,
	options?: OnlineOrderingOptionsModel,
): Array<OnlineOrderingFindingModel> {
	const findings: Array<OnlineOrderingFindingModel> = [];
	const blockedItemIds = new Set<number>();

	for (const line of lines) {
		if (!isOrderedQuantity(line.quantity) || blockedItemIds.has(line.itemId)) {
			continue;
		}

		if (!isOnlineOrderingBlockedSchedule(line.schedule, options)) {
			continue;
		}

		const schedule = normalizeDeaSchedule(line.schedule);

		blockedItemIds.add(line.itemId);
		findings.push({
			itemId: line.itemId,
			schedule,
			message: buildOnlineOrderingBlockedMessage(schedule),
		});
	}

	return findings;
}

/**
 * The lines that need a ship-to flag at all. When the ship-to address cannot
 * be read (missing or deleted), the server fails closed on these instead of
 * guessing, and raises one "shipping address not found" error for the order.
 */
export function linesRequiringScheduleFlags(
	lines: ReadonlyArray<ScheduleLineModel>,
): Array<number> {
	return lines
		.filter(
			(line) =>
				isOrderedQuantity(line.quantity) &&
				scheduleRestrictionFlag(line.schedule) != null,
		)
		.map((line) => line.itemId);
}

export type ControlledSubstanceUnitType =
	| 'unitsPurchased'
	| 'dollarAmount'
	| 'sizeCountQuantity';

export interface ControlledSubstanceLineModel extends ScheduleLineModel {
	price: number;
	/** Units per selling unit, for the UOM-quantity measure. Defaults to 1. */
	sizeCount?: number | null;
}

export interface ControlledSubstancePercentageInputModel {
	lines: ReadonlyArray<ControlledSubstanceLineModel>;
	/** The channel's maximum share, in percent. Null, undefined, 0 or less means none. */
	maximumPercent: number | null | undefined;
	unitType: ControlledSubstanceUnitType;
}

export interface ControlledSubstancePercentageFindingModel {
	level: OrderCheckFiringLevel;
	actualPercentage: number;
	maximumPercent: number;
	restrictedValue: number;
	totalValue: number;
	unitType: ControlledSubstanceUnitType;
}

/**
 * The share of the order that is a controlled substance (C2 through C5), by
 * dollars, units ordered or UOM quantity, may not exceed the maximum.
 */
export function checkControlledSubstancePercentage(
	input: ControlledSubstancePercentageInputModel,
	level: OrderCheckLevel | null | undefined,
): ControlledSubstancePercentageFindingModel | null {
	const maximumPercent = input.maximumPercent;

	if (
		!isOrderCheckOn(level) ||
		maximumPercent == null ||
		!(maximumPercent > 0)
	) {
		return null;
	}

	let totalValue = 0;
	let restrictedValue = 0;

	for (const line of input.lines) {
		if (!isOrderedQuantity(line.quantity)) {
			continue;
		}

		let value: number;

		if (input.unitType === 'dollarAmount') {
			value = line.quantity * (Number.isFinite(line.price) ? line.price : 0);
		} else if (input.unitType === 'sizeCountQuantity') {
			value = line.quantity * (line.sizeCount ?? 1);
		} else {
			value = line.quantity;
		}

		totalValue += value;

		if (isControlledSchedule(line.schedule)) {
			restrictedValue += value;
		}
	}

	if (!(totalValue > 0)) {
		return null;
	}

	const actualPercentage = (restrictedValue / totalValue) * 100;

	if (!(actualPercentage > maximumPercent)) {
		return null;
	}

	return {
		level,
		actualPercentage,
		maximumPercent,
		restrictedValue,
		totalValue,
		unitType: input.unitType,
	};
}
